import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { driversAPI } from '../../api/drivers';
import type { DriverSummary } from '../../types';
import { format } from 'date-fns';
import {
  DollarSign,
  TrendingUp,
  Calendar,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

const DriverCashSummary: React.FC = () => {
  const [summary, setSummary] = useState<DriverSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('today');

  useEffect(() => {
    fetchSummary();
  }, [timeRange]);

  const fetchSummary = async () => {
    setIsLoading(true);
    try {
      const response = await driversAPI.getDriverSummary();
      if (response.data) {
        setSummary(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch cash summary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: string;
    icon: React.ElementType;
    color: string;
    trend?: { value: number; isPositive: boolean };
    description?: string;
  }> = ({ title, value, icon: Icon, color, trend, description }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className={`flex-shrink-0 rounded-md p-3 ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          {trend && (
            <div className={`flex items-center text-sm ${trend.isPositive ? 'text-success-600' : 'text-danger-600'}`}>
              {trend.isPositive ? (
                <ArrowUpRight className="h-4 w-4 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 mr-1" />
              )}
              {trend.value}%
            </div>
          )}
        </div>
        <div className="mt-4">
          <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
          <dd className="text-2xl font-semibold text-gray-900">{value}</dd>
          {description && (
            <dd className="text-sm text-gray-500 mt-1">{description}</dd>
          )}
        </div>
      </div>
    </div>
  );

  const TransactionRow: React.FC<{
    orderNumber: string;
    amount: number;
    customer: string;
    time: string;
    type: 'delivery' | 'return';
  }> = ({ orderNumber, amount, customer, time, type }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-200 last:border-0">
      <div className="flex items-center">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
          type === 'delivery' ? 'bg-success-100' : 'bg-danger-100'
        }`}>
          {type === 'delivery' ? (
            <DollarSign className="h-5 w-5 text-success-600" />
          ) : (
            <CreditCard className="h-5 w-5 text-danger-600" />
          )}
        </div>
        <div className="ml-4">
          <div className="text-sm font-medium text-gray-900">
            {type === 'delivery' ? 'Delivery' : 'Cash Return'} • {orderNumber}
          </div>
          <div className="text-sm text-gray-500">{customer}</div>
        </div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-medium ${
          type === 'delivery' ? 'text-success-600' : 'text-danger-600'
        }`}>
          {type === 'delivery' ? '+' : '-'}${amount.toFixed(2)}
        </div>
        <div className="text-sm text-gray-500">{time}</div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Layout requiredRoles={['driver']}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout requiredRoles={['driver']}>
      <div className="space-y-6">
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Cash Summary</h1>
            <p className="mt-1 text-sm text-gray-600">
              Track your cash collections and returns
            </p>
          </div>
          
          <div className="mt-4 sm:mt-0">
            <div className="inline-flex rounded-md shadow-sm">
              <button
                onClick={() => setTimeRange('today')}
                className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                  timeRange === 'today'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setTimeRange('week')}
                className={`px-4 py-2 text-sm font-medium -ml-px ${
                  timeRange === 'week'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border'
                }`}
              >
                This Week
              </button>
              <button
                onClick={() => setTimeRange('month')}
                className={`px-4 py-2 text-sm font-medium -ml-px rounded-r-md ${
                  timeRange === 'month'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border'
                }`}
              >
                This Month
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Cash Collected"
            value={`$${(summary?.today.total_cash_collected || 0).toFixed(2)}`}
            icon={DollarSign}
            color="bg-success-500"
            description={format(new Date(), 'MMM d, yyyy')}
          />
          <StatCard
            title="Cash on Hand"
            value={`$${(summary?.today.cash_currently_held || 0).toFixed(2)}`}
            icon={CreditCard}
            color="bg-warning-500"
            description="To return to store"
          />
          <StatCard
            title="Deliveries"
            value={summary?.today.deliveries_completed.toString() || '0'}
            icon={TrendingUp}
            color="bg-primary-500"
            description="Completed today"
          />
          <StatCard
            title="Avg. per Delivery"
            value={`$${(
              (summary?.today.total_cash_collected || 0) / 
              Math.max(summary?.today.deliveries_completed || 1, 1)
            ).toFixed(2)}`}
            icon={Calendar}
            color="bg-blue-500"
            description="Average amount"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Transactions */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Recent Transactions
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Your latest cash collections
              </p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              {/* Mock transaction data - in real app, fetch from API */}
              <TransactionRow
                orderNumber="ORD-20240115-000123"
                amount={149.99}
                customer="Jane Smith"
                time="2:30 PM"
                type="delivery"
              />
              <TransactionRow
                orderNumber="ORD-20240115-000122"
                amount={89.50}
                customer="John Doe"
                time="1:45 PM"
                type="delivery"
              />
              <TransactionRow
                orderNumber="ORD-20240115-000121"
                amount={20.00}
                customer="Bob Wilson"
                time="12:15 PM"
                type="return"
              />
              <TransactionRow
                orderNumber="ORD-20240115-000120"
                amount={199.99}
                customer="Alice Johnson"
                time="11:00 AM"
                type="delivery"
              />
            </div>
            <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
              <button className="text-sm font-medium text-primary-600 hover:text-primary-500">
                View all transactions →
              </button>
            </div>
          </div>

          {/* Cash Return Schedule */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Cash Return Schedule
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                When to return cash to store
              </p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-success-50 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-success-900">
                      Daily Return
                    </div>
                    <div className="text-sm text-success-700">
                      Return cash at end of each day
                    </div>
                  </div>
                  <div className="text-sm text-success-900 font-medium">
                    ${(summary?.today.cash_currently_held || 0).toFixed(2)}
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  <p className="mb-2">
                    <strong>Cash Handling Guidelines:</strong>
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Count cash with store manager present</li>
                    <li>Get signed receipt for all cash returns</li>
                    <li>Report discrepancies immediately</li>
                    <li>Never leave cash unattended in vehicle</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-primary-50 rounded-lg">
                  <div className="text-sm font-medium text-primary-900">
                    Next Cash Return
                  </div>
                  <div className="text-sm text-primary-700">
                    Today at 6:00 PM • Store closing time
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DriverCashSummary;